import type { ReportLayoutConfig, SectionBuild } from '../models/layoutConfig.types'

import {
  demographicDeviceBuild,
  metaAudienceBuild,
  tiktokTargetingPerformanceBuild,
} from './paidSocialTargeting'

// v3 `configs/Audience/DemographicAnalysis.js` (249 ln after c8244add). Bubble-chart sections: v3 built
// `fieldValues` role maps via buildChartFieldValues — dropped, v4 derives chart field roles
// positionally and the v3 order already matches (dimensions as given; metrics → [x, y, size]).
// Location carried its chart fields ONLY inside `fieldValues` (no chartDimensions/chartMetrics
// props) — that content ports to chartDimensions/chartMetrics so the chart keeps its fields;
// it declared no `defaultChart`, so no chartType is pinned (host default), same as v3.
// Both id templates stringify the Overall tab's undefined channel — kept verbatim.

const DEMOGRAPHIC_ANALYSIS_REPORT_ID = 70

const ageGenderBuild: SectionBuild = ({ tab }) => ({
  id: `70-age-gender-${tab.channel}`,
  reportId: DEMOGRAPHIC_ANALYSIS_REPORT_ID,
  scheduleTabs: tab.tabs ?? [],
  chartDimensions: ['user_gender', 'age'],
  chartMetrics: ['cost', 'conversions_value', 'conversions'],
  chartType: 'bubble',
  sortField: 'cost',
  view: 'table',
  composition: 'chart-table',
  breakdown: 'user_gender',
  filters: tab.channel
    ? [{ field: 'marketing_channel', match_mode: 'equals', value: tab.channel }]
    : [],
})

const locationBuild: SectionBuild = ({ tab }) => ({
  id: `70-location-${tab.channel}`,
  reportId: DEMOGRAPHIC_ANALYSIS_REPORT_ID,
  scheduleTabs: tab.tabs ?? [],
  chartDimensions: ['city_name', 'country_name'],
  chartMetrics: ['cost', 'conversions_value', 'conversions'],
  sortField: 'cost',
  view: 'table',
  composition: 'chart-table',
  filters: tab.channel
    ? [{ field: 'marketing_channel', match_mode: 'equals', value: tab.channel }]
    : [],
})

export const DEMOGRAPHIC_ANALYSIS: ReportLayoutConfig = [
  {
    name: 'Age Gender',
    labelKey: 'reports.demographicAnalysis.tabs.ageGender',
    sections: [
      {
        id: '70-age-gender-overview',
        reportId: DEMOGRAPHIC_ANALYSIS_REPORT_ID,
        tabs: [
          { name: 'Overall', tabs: ['age_gender_overview'] },
          { name: 'Google', channel: 'google_ads', tabs: ['google_ads_age_gender'] },
          { name: 'Meta', channel: 'meta_ads', tabs: ['meta_ads_age_gender'] },
          { name: 'Tiktok', channel: 'tiktok_ads', tabs: ['tiktok_ads_age_gender'] },
        ],
        build: ageGenderBuild,
      },
    ],
  },
  {
    name: 'Location',
    labelKey: 'reports.demographicAnalysis.tabs.location',
    sections: [
      {
        id: '70-location-overview',
        reportId: DEMOGRAPHIC_ANALYSIS_REPORT_ID,
        tabs: [
          { name: 'Overall', tabs: ['location_overview'] },
          { name: 'Google', channel: 'google_ads', tabs: ['google_ads_location'] },
          { name: 'Meta', channel: 'meta_ads', tabs: ['meta_ads_location'] },
          { name: 'Tiktok', channel: 'tiktok_ads', tabs: ['tiktok_ads_location'] },
        ],
        build: locationBuild,
      },
    ],
  },
  {
    name: 'Audience',
    labelKey: 'reports.demographicAnalysis.tabs.audience',
    defaultPreset: 'Yesterday',
    sections: [
      {
        id: '19-audiences',
        titleKey: 'reports.demographicAnalysis.audienceSegments.title',
        reportId: 19,
        hiddenCompare: true,
        hideChannels: true,
        build: metaAudienceBuild,
        tabs: [{ name: 'Audience Segments', tabs: ['metaads_customaudiences'] }],
      },
    ],
  },
  {
    name: 'Device',
    labelKey: 'reports.demographicAnalysis.tabs.device',
    sections: [
      {
        id: '19-device',
        build: demographicDeviceBuild,
        tabs: [
          { name: 'Meta Ads', reportId: 19, tabs: ['metaads_analysis_device'] },
          {
            name: 'Tiktok Ads',
            reportId: 45,
            tabs: ['tiktokads_demographic_device_data'],
          },
        ],
      },
    ],
  },
  {
    name: 'Behavior',
    labelKey: 'reports.demographicAnalysis.tabs.behavior',
    sections: [
      {
        id: '45-behavior',
        build: tiktokTargetingPerformanceBuild,
        tabs: [
          {
            name: 'Tiktok Ads',
            reportId: 45,
            tabs: ['tiktokads_demographic_behavior_id_data'],
          },
        ],
      },
    ],
  },
  {
    name: 'Interest',
    labelKey: 'reports.demographicAnalysis.tabs.interest',
    sections: [
      {
        id: '45-interest',
        build: tiktokTargetingPerformanceBuild,
        tabs: [
          {
            name: 'Tiktok Ads',
            reportId: 45,
            tabs: ['tiktokads_demographic_interest_category_data'],
          },
        ],
      },
    ],
  },
  {
    name: 'Search Terms',
    labelKey: 'reports.demographicAnalysis.tabs.searchTerms',
    sections: [
      {
        id: '45-search-terms',
        build: tiktokTargetingPerformanceBuild,
        tabs: [
          {
            name: 'Tiktok Ads',
            reportId: 45,
            tabs: ['tiktokads_demographic_search_terms_data'],
          },
        ],
      },
    ],
  },
]
